import { formatDate } from '../utils/formatDate';
import usePageTitle from '../hooks/usePageTitle';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { invitesService, Invite, InvitableRole } from '../services/invites';
import { clientsService, consultanciesService } from '../services';
import { Client, Consultancy } from '../types';

const Invites: React.FC = () => {
  usePageTitle('Invites');
  const {
    currentUser,
    isConsultant,
    isClient,
    isH2okayAdmin,
  } = useAuth();
  const { showSuccess, showError } = useToast();

  // Role-driven invite affordances. The backend enforces the hard
  // rules; the frontend just hides nonsensical options.
  //   admin       -> invite any role; clients are scoped per consultancy
  //   consultant  -> invite consultant/client/facility_manager (within
  //                  their consultancy)
  //   client      -> invite only facility_managers in their own org
  //   facility_manager -> no invite power; sees the "not allowed" stub
  const allowedRoles: InvitableRole[] = useMemo(() => {
    if (isH2okayAdmin)
      return ['facility_manager', 'client', 'consultant', 'h2okay_admin'];
    if (isConsultant) return ['facility_manager', 'client', 'consultant'];
    if (isClient) return ['facility_manager'];
    return [];
  }, [isH2okayAdmin, isConsultant, isClient]);

  const canInvite = allowedRoles.length > 0;

  const [invites, setInvites] = useState<Invite[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [consultancies, setConsultancies] = useState<Consultancy[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InvitableRole>(allowedRoles[0] ?? 'facility_manager');
  const [clientId, setClientId] = useState('');
  const [consultancyId, setConsultancyId] = useState('');
  const [adminConfirmed, setAdminConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Reset the role state if the user's role changed (e.g. after a /me
  // refresh) and the previously-selected option is no longer allowed.
  useEffect(() => {
    if (allowedRoles.length > 0 && !allowedRoles.includes(role)) {
      setRole(allowedRoles[0]);
    }
  }, [allowedRoles, role]);

  // Clients can only invite into their own org, so pre-fill it.
  useEffect(() => {
    if (isClient && currentUser?.client_id) {
      setClientId(currentUser.client_id);
    }
  }, [isClient, currentUser?.client_id]);

  const reload = async () => {
    setLoading(true);
    try {
      // Admins also need the consultancy list so they can target invites
      // to a specific consultancy. Other roles never see that picker, so
      // we save the round-trip.
      const [list, allClients, allConsultancies] = await Promise.all([
        invitesService.list(),
        clientsService.getClients(),
        isH2okayAdmin
          ? consultanciesService.list({ limit: 1000 })
          : Promise.resolve([] as Consultancy[]),
      ]);
      setInvites(list);
      setClients(allClients);
      setConsultancies(allConsultancies);
      if (
        isH2okayAdmin &&
        allConsultancies.length > 0 &&
        !consultancyId
      ) {
        setConsultancyId(allConsultancies[0].id);
      }
    } catch (err) {
      showError(
        'Could not load invites',
        err instanceof Error ? err.message : 'Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canInvite) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canInvite]);

  if (!canInvite) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Invites</h1>
        </div>
        <div className="page-content">
          <p>You don&rsquo;t have permission to invite users.</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || (role === 'h2okay_admin' && !adminConfirmed)) return;
    setSubmitting(true);
    try {
      await invitesService.create({
        email: email.trim().toLowerCase(),
        role,
        client_id:
          role === 'consultant' || role === 'h2okay_admin'
            ? undefined
            : clientId || undefined,
        consultancy_id:
          isH2okayAdmin && role === 'consultant'
            ? consultancyId || undefined
            : undefined,
      });
      showSuccess('Invite sent', `Magic-link email sent to ${email}.`);
      setEmail('');
      setAdminConfirmed(false);
      setClientId('');
      await reload();
    } catch (err) {
      showError(
        'Invite failed',
        err instanceof Error ? err.message : 'Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await invitesService.revoke(id);
      setInvites((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      showError(
        'Could not revoke invite',
        err instanceof Error ? err.message : 'Please try again.',
      );
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Invites</h1>
        <p>Send a magic-link invite to onboard a new user.</p>
      </div>

      <div className="page-content" style={{ display: 'grid', gap: '1.5rem' }}>
        <div className="card" style={{ padding: '1.5rem' }}>
          <h2 style={{ marginTop: 0 }}>New invite</h2>
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '0.75rem' }}>
            <label>
              <div style={{ marginBottom: '0.25rem', fontWeight: 500 }}>Email</div>
              <input
                className="form-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="invitee@example.com"
                style={{ width: '100%' }}
              />
            </label>
            <label>
              <div style={{ marginBottom: '0.25rem', fontWeight: 500 }}>Role</div>
              <select
                className="form-input"
                value={role}
                onChange={(e) => { setRole(e.target.value as InvitableRole); setAdminConfirmed(false); }}
                style={{ width: '100%' }}
                disabled={allowedRoles.length === 1}
              >
                {allowedRoles.includes('facility_manager') && (
                  <option value="facility_manager">Facility manager</option>
                )}
                {allowedRoles.includes('client') && (
                  <option value="client">Client</option>
                )}
                {allowedRoles.includes('consultant') && (
                  <option value="consultant">Consultant</option>
                )}
                {allowedRoles.includes('h2okay_admin') && (
                  <option value="h2okay_admin">H2Okay admin</option>
                )}
              </select>
            </label>
            {role === 'h2okay_admin' && <label><input type="checkbox" checked={adminConfirmed} onChange={e => setAdminConfirmed(e.target.checked)} required /> This person will have full access to every consultancy</label>}
            {(role === 'client' || role === 'facility_manager') && (
              <label>
                <div style={{ marginBottom: '0.25rem', fontWeight: 500 }}>Client</div>
                <select
                  className="form-input"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  style={{ width: '100%' }}
                  required
                  disabled={isClient}
                >
                  {!isClient && <option value="">Select a client…</option>}
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {isClient && (
                  <div
                    style={{
                      marginTop: '0.25rem',
                      fontSize: '0.8125rem',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Clients can only invite facility managers within their own
                    organisation.
                  </div>
                )}
              </label>
            )}
            {isH2okayAdmin && role === 'consultant' && (
              <label>
                <div style={{ marginBottom: '0.25rem', fontWeight: 500 }}>
                  Consultancy
                </div>
                <select
                  className="form-input"
                  value={consultancyId}
                  onChange={(e) => setConsultancyId(e.target.value)}
                  style={{ width: '100%' }}
                  required
                >
                  <option value="">Select a consultancy…</option>
                  {consultancies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <div
                  style={{
                    marginTop: '0.25rem',
                    fontSize: '0.8125rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  The consultant will be able to see clients owned by this
                  consultancy only.
                </div>
              </label>
            )}
            {role === 'h2okay_admin' && (
              <div
                style={{
                  fontSize: '0.8125rem',
                  color: 'var(--text-secondary)',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid var(--border, #e3e8ef)',
                  borderRadius: '6px',
                }}
              >
                H2Okay admins sit above all consultancies and have no
                client/consultancy scope.
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || (role === 'h2okay_admin' && !adminConfirmed)}
              style={{ justifySelf: 'start' }}
            >
              {submitting ? 'Sending…' : 'Send invite'}
            </button>
          </form>
        </div>

        <div className="card" style={{ padding: '1.5rem' }}>
          <h2 style={{ marginTop: 0 }}>Pending and recent invites</h2>
          {loading ? (
            <p>Loading…</p>
          ) : invites.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>No invites yet.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem' }}>Email</th>
                  <th style={{ padding: '0.5rem' }}>Role</th>
                  <th style={{ padding: '0.5rem' }}>Status</th>
                  <th style={{ padding: '0.5rem' }}>Sent</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '0.5rem' }}>{inv.email}</td>
                    <td style={{ padding: '0.5rem' }}>{inv.role}</td>
                    <td style={{ padding: '0.5rem' }}>
                      {inv.redeemed_at ? 'Redeemed' : 'Pending'}
                    </td>
                    <td style={{ padding: '0.5rem' }}>
                      {formatDate(inv.created_at)}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                      {!inv.redeemed_at && (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleRevoke(inv.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Invites;
